import React, { useEffect, useState, useRef } from 'react';
import { Box, TextField, Typography, CircularProgress } from '@mui/material';
import { http } from '../../../../api/http';

interface DocumentNumberFieldBProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  fechaExtraccion?: string;
  idOrigenProducto?: number; // Product type for separate sequences (1=Type A, 2=Type B)
}

const DocumentNumberFieldB: React.FC<DocumentNumberFieldBProps> = ({
  label,
  value,
  onChange,
  error,
  required = false,
  fechaExtraccion,
  idOrigenProducto = 2, // Default to Type B
}) => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchedKey = useRef<string>('');

  // Parse the current value to extract the document number
  useEffect(() => {
    if (value) {
      const match = value.match(/^(\d+)-(\d{4})-(\d{2})\/SEC$/);
      if (match) {
        setDocumentNumber(match[1]);
        setYear(match[2]);
        setMonth(match[3]);
      } else {
        // If value doesn't match format, try to extract just the number at the beginning
        const numberMatch = value.match(/^(\d+)/);
        setDocumentNumber(numberMatch ? numberMatch[1] : '');
      }
    } else {
      setDocumentNumber('');
    }
  }, [value]);

  // Fetch next correlative number from backend when fechaExtraccion changes
  useEffect(() => {
    const fetchNextNumber = async () => {
      if (fechaExtraccion) {
        const date = new Date(fechaExtraccion);
        const newYear = date.getFullYear();
        const newMonth = date.getMonth() + 1;
        const fetchKey = `${newYear}-${newMonth}-${idOrigenProducto}`;

        // Avoid duplicate fetches for same year-month-type
        if (lastFetchedKey.current === fetchKey) {
          return;
        }

        setYear(newYear.toString());
        setMonth(newMonth.toString().padStart(2, '0'));

        setIsLoading(true);
        try {
          // Include idOrigenProducto to keep Type A and Type B sequences separate
          const response = await http.get(`/documento/next-number?year=${newYear}&month=${newMonth}&idOrigenProducto=${idOrigenProducto}`);
          const nextNumber = response.data;
          lastFetchedKey.current = fetchKey;

          // Extract just the correlative number from the full response
          const match = nextNumber.match(/^(\d+)/);
          if (match) {
            setDocumentNumber(match[1]);
            onChange(nextNumber);
            console.log(`Fetched next DERC type B number: ${nextNumber}`);
          }
        } catch (err) {
          console.error('Error fetching next document number:', err);
          // Fallback to 0001 if API fails
          const paddedNumber = '0001';
          setDocumentNumber(paddedNumber);
          const fullDocumentNumber = `${paddedNumber}-${newYear}-${newMonth.toString().padStart(2, '0')}/SEC`;
          onChange(fullDocumentNumber);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchNextNumber();
  }, [fechaExtraccion, onChange, idOrigenProducto]);

  const displayValue = documentNumber && year && month
    ? `${documentNumber}-${year}-${month}/SEC`
    : '';

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: 'text.secondary',
          fontSize: '0.875rem'
        }}
      >
        {label} {required && <span style={{ color: 'error.main' }}>*</span>}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={isLoading ? '' : documentNumber}
          size="small"
          sx={{ width: '80px' }}
          InputProps={{
            readOnly: true,
            style: {
              textAlign: 'center',
              backgroundColor: '#e8f5e8',
              fontWeight: 600,
              color: '#2e7d32'
            },
            endAdornment: isLoading ? <CircularProgress size={16} /> : null
          }}
          placeholder="0000"
          error={!!error}
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>

        <TextField
          value={year}
          size="small"
          sx={{ width: '80px' }}
          InputProps={{
            readOnly: true,
            style: { textAlign: 'center', backgroundColor: '#f5f5f5' }
          }}
          placeholder="YYYY"
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>

        <TextField
          value={month}
          size="small"
          sx={{ width: '50px' }}
          InputProps={{
            readOnly: true,
            style: { textAlign: 'center', backgroundColor: '#f5f5f5' }
          }}
          placeholder="MM"
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          /SEC
        </Typography>
      </Box>

      {displayValue && !isLoading && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: 'success.main',
            fontStyle: 'italic',
            fontWeight: 600
          }}
        >
          Número generado: {displayValue}
        </Typography>
      )}

      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          display: 'block',
          color: 'info.main',
          fontSize: '0.75rem',
          fontWeight: 500
        }}
      >
        Numeración correlativa por mes - DERC Tipo B
      </Typography>

      {error && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: 'error.main'
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DocumentNumberFieldB;
